import { energyConversion } from "utils/constants/energyConversion";
import { emissionFactorsNIR } from "utils/emissions/getEmissionFactors";
import { mbtuToGJ } from "utils/results/energyPlus";
import { provinceCodeMap } from "utils/weather";

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const ANNUAL = {
    electricity: "Fuel Use: Electricity: Total (MBtu)",
    naturalGas: "Fuel Use: Natural Gas: Total (MBtu)",
    propane: "Fuel Use: Propane: Total (MBtu)",
    oil: "Fuel Use: Fuel Oil: Total (MBtu)",
    peak: "Peak Electricity: Annual Total (W)",
    woodPellets: "Fuel Use: Wood Pellets: Total (MBtu)",
    woodCord: "Fuel Use: Wood Cord: Total (MBtu)",
};

const FUELS = {
    electricity: { annualKey: ANNUAL.electricity, perGj: energyConversion.elecGJperkWh, unit: "kWh" },
    naturalGas: { annualKey: ANNUAL.naturalGas, perGj: energyConversion.ngGJperm3, unit: "m³" },
    propane: { annualKey: ANNUAL.propane, perGj: energyConversion.propaneGJperL, unit: "L" },
    oil: { annualKey: ANNUAL.oil, perGj: energyConversion.oilGJperL, unit: "L" },
};

export const SENSITIVITY = {
    electricity: { min: 5, max: 30, step: 1, unit: "¢/kWh" },
    naturalGas: { min: 10, max: 100, step: 1, unit: "¢/m³" },
    propane: { min: 60, max: 110, step: 5, unit: "¢/L" },
    oil: { min: 100, max: 250, step: 5, unit: "¢/L" },
};

export const temperatureId = (temperature) => {
    const text = String(Number(temperature));
    return text.startsWith("-") ? `m${text.slice(1)}` : text;
};

const numberOrZero = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const scheduleCell = (schedule, month, hour) => {
    const period = Number(schedule?.[month]?.[hour]);
    return Number.isFinite(period) ? period : 0;
};

const periodPriceDollars = (periodPrices, period) => {
    if (!period) return 0;
    const raw = Array.isArray(periodPrices) ? periodPrices[period - 1] : periodPrices?.[`period${period}`];
    const cents = Number(raw);
    if (!Number.isFinite(cents)) return 0;
    return parseFloat(cents.toFixed(3)) / 100;
};

// Same fuel-cost formula as calcOperatingCosts: energy cost, then fixed monthly
// charge, then tax and rebate on that subtotal. A fuel with no consumption is $0.
const fuelBill = (quantity, unitCost, price = {}) => {
    if (!quantity) return 0;
    const fixed = numberOrZero(price.dollarsPerMonth) * 12;
    const subtotal = quantity * unitCost + fixed;
    const tax = subtotal * (numberOrZero(price.tax) / 100);
    const rebate = subtotal * (numberOrZero(price.preTaxRebate) / 100);
    return subtotal + tax - rebate;
};

const hourlyElectricityCost = (hourlyKwh, timeOfUse = {}) => {
    if (!Array.isArray(hourlyKwh) || hourlyKwh.length !== 8760) return null;
    const { periodPrices, weekdayScheduleTOU, weekendScheduleTOU } = timeOfUse;
    let total = 0;
    let weekday = 0;
    let index = 0;

    DAYS_IN_MONTH.forEach((days, month) => {
        for (let day = 0; day < days; day += 1) {
            const schedule = weekday === 0 || weekday === 6 ? weekendScheduleTOU : weekdayScheduleTOU;
            for (let hour = 0; hour < 24; hour += 1) {
                total +=
                    numberOrZero(hourlyKwh[index]) *
                    periodPriceDollars(periodPrices, scheduleCell(schedule, month, hour));
                index += 1;
            }
            weekday = (weekday + 1) % 7;
        }
    });

    return total;
};

const quantityFromMbtu = (mbtu, fuelKey) => {
    const spec = FUELS[fuelKey];
    if (!spec) return 0;
    return mbtuToGJ(numberOrZero(mbtu)) / spec.perGj;
};

const pricedFuel = ({ mbtu, fuelKey, price, rateType, timeOfUse, hourlyKwh, override }) => {
    const quantity = quantityFromMbtu(mbtu, fuelKey);
    if (fuelKey === "electricity" && rateType === "timeOfUse" && !override) {
        const hourlyCost = hourlyElectricityCost(hourlyKwh, timeOfUse);
        if (hourlyCost != null) return fuelBill(1, hourlyCost, price);
    }

    const unitCost = override?.dollarsPerUnit != null ? override.dollarsPerUnit : numberOrZero(price?.dollarsPerUnit);
    return fuelBill(quantity, unitCost, price);
};

// Same NIR 2026 factors as the EnergyPlus results: grams per fuel unit, reported as tonnes.
const nirTonnes = (mbtu, perGj, gramsPerUnit) => {
    if (!gramsPerUnit) return 0;
    return (mbtuToGJ(numberOrZero(mbtu)) / perGj) * gramsPerUnit * 1e-6;
};

const nirEmissionsTonnes = (annual, regionId) => {
    const factors = emissionFactorsNIR[provinceCodeMap[regionId] || "ON"];
    if (!factors) return null;
    const woodGj = mbtuToGJ(numberOrZero(annual[ANNUAL.woodPellets])) + mbtuToGJ(numberOrZero(annual[ANNUAL.woodCord]));
    return (
        nirTonnes(annual[ANNUAL.electricity], energyConversion.elecGJperkWh, factors.elec) +
        nirTonnes(annual[ANNUAL.naturalGas], energyConversion.ngGJperm3, factors.ng) +
        nirTonnes(annual[ANNUAL.propane], energyConversion.propaneGJperL, factors.propane) +
        nirTonnes(annual[ANNUAL.oil], energyConversion.oilGJperL, factors.oil) +
        (woodGj / energyConversion.woodPelletsGJperkg) * factors.woodStove * 1e-6
    );
};

const heatLossAtOutdoorTemp = (heatLoss, designTemp, outdoorTemp) => {
    if (!heatLoss || designTemp == null || outdoorTemp >= 16) return 0;
    return Math.max(0, (heatLoss * (16 - outdoorTemp)) / (16 - designTemp));
};

export const buildTemperatureRows = ({
    resultsById = {},
    temperatures = [],
    fuelPrices = {},
    timeOfUse,
    electricityRateType,
    capitalCosts = {},
    lifetimeYears = 15,
    heatLossWatts,
    designTempC,
    backupFuelKey,
    regionId,
    priceOverrides = {},
} = {}) => {
    const lifetime = numberOrZero(lifetimeYears) || 15;
    const capital = numberOrZero(capitalCosts.heatPump) + numberOrZero(capitalCosts.furnace);
    const backupKey = FUELS[backupFuelKey] ? backupFuelKey : "";
    const backupUnitCost =
        backupKey && priceOverrides.backupCentsPerUnit != null ? priceOverrides.backupCentsPerUnit / 100 : null;

    return temperatures
        .map((temperature) => {
            const result = resultsById[temperatureId(temperature)];
            if (!result?.annual) return null;
            const annual = result.annual;
            const electricity = pricedFuel({
                mbtu: annual[ANNUAL.electricity],
                fuelKey: "electricity",
                price: fuelPrices.electricity,
                rateType: electricityRateType,
                timeOfUse,
                hourlyKwh: result.hourlyElectricityKwh,
                override:
                    priceOverrides.electricityDollarsPerKwh != null
                        ? { dollarsPerUnit: priceOverrides.electricityDollarsPerKwh }
                        : null,
            });
            const naturalGas = pricedFuel({
                mbtu: annual[ANNUAL.naturalGas],
                fuelKey: "naturalGas",
                price: fuelPrices.naturalGas,
                override:
                    backupKey === "naturalGas" && backupUnitCost != null ? { dollarsPerUnit: backupUnitCost } : null,
            });
            const propane = pricedFuel({
                mbtu: annual[ANNUAL.propane],
                fuelKey: "propane",
                price: fuelPrices.propane,
                override: backupKey === "propane" && backupUnitCost != null ? { dollarsPerUnit: backupUnitCost } : null,
            });
            const oil = pricedFuel({
                mbtu: annual[ANNUAL.oil],
                fuelKey: "oil",
                price: fuelPrices.oil,
                override: backupKey === "oil" && backupUnitCost != null ? { dollarsPerUnit: backupUnitCost } : null,
            });
            const operatingCost = electricity + naturalGas + propane + oil;
            const capacityWatts = heatLossAtOutdoorTemp(heatLossWatts, designTempC, temperature);

            return {
                temperature,
                operatingCost,
                totalCost: capital + operatingCost * lifetime,
                emissionsTonnes: nirEmissionsTonnes(annual, regionId),
                electricityKwh: quantityFromMbtu(annual[ANNUAL.electricity], "electricity"),
                backupQuantity: backupKey ? quantityFromMbtu(annual[FUELS[backupKey].annualKey], backupKey) : null,
                backupUnit: backupKey ? FUELS[backupKey].unit : "",
                peakKw: numberOrZero(annual[ANNUAL.peak]) / 1000,
                capacityKw: heatLossWatts && designTempC != null ? capacityWatts / 1000 : null,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.temperature - b.temperature);
};

export const lowestOperatingCost = (rows = []) => {
    if (!rows.length) return null;
    return rows.reduce((lowest, row) => (row.operatingCost <= lowest.operatingCost ? row : lowest));
};

/** Lowest numeric value for a metric; on a tie, the warmest switchover temperature wins. */
export const lowestForMetric = (rows = [], metric) => {
    if (!rows.length || !metric) return null;
    return rows.reduce((best, row) => {
        const value = metricValue(row, metric);
        const bestValue = metricValue(best, metric);
        if (!Number.isFinite(value)) return best;
        if (!Number.isFinite(bestValue)) return row;
        if (value < bestValue) return row;
        if (value === bestValue && row.temperature >= best.temperature) return row;
        return best;
    });
};

export const initialSensitivityPrice = (fuelKey, fuelPrices = {}) => {
    const range = SENSITIVITY[fuelKey];
    if (!range) return null;
    const dollars = numberOrZero(fuelPrices[fuelKey]?.dollarsPerUnit);
    if (!dollars) return range.min;
    const cents = Math.round(dollars * 100);
    const clamped = Math.min(range.max, Math.max(range.min, cents));
    const steps = Math.round((clamped - range.min) / range.step);
    return range.min + steps * range.step;
};

export const metricValue = (row, metric) => row?.[metric];

export const METRICS = [
    { value: "operatingCost", label: "Operating cost", unit: "$/yr", money: true },
    { value: "totalCost", label: "Total cost of ownership", unit: "$", money: true },
    { value: "emissionsTonnes", label: "Emissions", unit: "tCO₂", digits: 2 },
    { value: "electricityKwh", label: "Electricity", unit: "kWh", digits: 0 },
    { value: "backupQuantity", label: "Backup fuel", unit: "", digits: 0 },
    { value: "peakKw", label: "Peak electrical load", unit: "kW", digits: 2 },
    { value: "capacityKw", label: "Heat pump capacity", unit: "kW", digits: 1 },
];
